import { create } from 'zustand';
import toast from 'react-hot-toast';
import { apiClient } from '../lib/api';

/**
 * Asset Hub settings — loaded from and saved to the assets service
 * (GET/PUT /assets/asset-settings → assets.asset_settings). Nothing is kept in
 * localStorage. S3 settings are server config (/assets/storage/*); the secret
 * key is sent once when saving and never stored in the browser.
 * Custom category fields live on the categories API (assetCatalogStore).
 */

export interface AssetNumberingConfig {
  prefix: string;
  padding_digits: number;
  next_number: number;
  include_category_code: boolean;
  separator: string;
  include_year: boolean;
  allow_manual_override: boolean;
  barcode_symbology: 'Code128' | 'QR Code' | 'DataMatrix' | 'Code39';
}

export interface WarrantyPolicyConfig {
  default_hardware_warranty_months: number;
  warranty_alert_threshold_days: number;
  preventive_maintenance_cycle_days: number;
  auto_notify_vendor_on_warranty_claim: boolean;
  auto_create_ticket_on_warranty_expiry: boolean;
  require_vendor_rma_number: boolean;
  grace_period_days: number;
  eol_notification_months: number;
}

export interface RequisitionPolicyConfig {
  auto_approve_low_cost: boolean;
  low_cost_threshold_amount: number;
  require_manager_approval: boolean;
  require_it_director_approval_above: number;
  allow_employee_self_selection: boolean;
  max_active_requests_per_user: number;
  auto_create_support_ticket: boolean;
  require_return_for_replacement: boolean;
  restricted_categories: string[];
}

export interface DepreciationPolicyConfig {
  method: 'Straight Line' | 'Declining Balance' | 'Sum of Years' | 'No Depreciation';
  default_salvage_percentage: number;
  laptop_useful_life_years: number;
  server_useful_life_years: number;
  mobile_useful_life_years: number;
  peripheral_useful_life_years: number;
  fiscal_year_start_month: string;
  minimum_depreciable_cost: number;
  depreciation_frequency: 'Monthly' | 'Quarterly' | 'Annually';
}

export interface StockPolicyConfig {
  low_stock_threshold: number;
  reorder_quantity: number;
  auto_notify_low_stock: boolean;
  default_delivery_location: string;
  allow_negative_inventory: boolean;
}

export interface MaintenancePolicyConfig {
  response_sla_hours: number;
  resolution_sla_days: number;
  critical_asset_sla_hours: number;
  auto_create_maintenance_ticket: boolean;
  require_vendor_for_repairs: boolean;
}

export interface ProcurementPolicyConfig {
  po_prefix: string;
  approval_required_above: number;
  default_currency: string;
  default_payment_terms: string;
  terms_and_conditions: string;
  auto_create_ticket_on_po: boolean;
}

export interface AuditPolicyConfig {
  audit_cycle_days: number;
  require_custody_signature: boolean;
  allow_employee_self_audit: boolean;
  flag_missing_after_days: number;
}

export interface NotificationPolicyConfig {
  notify_employee_on_assignment: boolean;
  notify_employee_on_return: boolean;
  notify_manager_on_asset_loss: boolean;
  email_alert_recipients: string[];
}

export interface AssetStorageConfig {
  s3_bucket: string;
  s3_region: string;
  s3_prefix: string;
  access_key_id?: string;
  secret_access_key?: string;
  s3_endpoint?: string;
  force_path_style?: boolean;
  max_file_size_mb: number;
  direct_upload_enabled: boolean;
  is_configured?: boolean;
  access_key_preview?: string;
}

export interface AssetSettings {
  numbering: AssetNumberingConfig;
  warranty: WarrantyPolicyConfig;
  requisition: RequisitionPolicyConfig;
  depreciation: DepreciationPolicyConfig;
  stock: StockPolicyConfig;
  maintenance: MaintenancePolicyConfig;
  procurement: ProcurementPolicyConfig;
  audit: AuditPolicyConfig;
  notifications: NotificationPolicyConfig;
}

/** Preview of the next asset code for the given numbering settings. */
export function formatAssetCode(n: AssetNumberingConfig, categoryCode = '') {
  const parts: string[] = [n.prefix ? n.prefix.toUpperCase() : 'TBA'];
  if (n.include_category_code && categoryCode) {
    const cat = categoryCode.replace(/[^A-Za-z]/g, '').slice(0, 3).toUpperCase();
    if (cat) parts.push(cat);
  }
  if (n.include_year) {
    parts.push(String(new Date().getFullYear()));
  }
  const padded = String(n.next_number).padStart(Math.max(1, n.padding_digits), '0');
  parts.push(padded);
  return parts.join(n.separator ?? '-');
}

const EMPTY_STORAGE: AssetStorageConfig = {
  s3_bucket: '',
  s3_region: '',
  s3_prefix: '',
  max_file_size_mb: 25,
  direct_upload_enabled: true,
};

const errorOf = (e: any, fallback: string) => e?.response?.data?.message || e?.message || fallback;

interface AssetSettingsState {
  settings: AssetSettings | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  storage: AssetStorageConfig;
  /** True when the caller's role may not view storage settings (403). */
  storageForbidden: boolean;
  isTestingStorage: boolean;
  storageTestResult: { success: boolean; message: string; latencyMs?: number; mode?: string } | null;

  fetchSettings: () => Promise<void>;
  /** Save one or more sections; returns the saved settings. Throws on validation errors. */
  saveSettings: (patch: Partial<{ [K in keyof AssetSettings]: Partial<AssetSettings[K]> }>, successMessage?: string) => Promise<AssetSettings>;

  fetchBackendStorageConfig: () => Promise<void>;
  saveBackendStorageConfig: (config?: Partial<AssetStorageConfig>) => Promise<void>;
  testStorageConnection: () => Promise<{ success: boolean; message: string; latencyMs?: number; mode?: string }>;
}

export const useAssetSettingsStore = create<AssetSettingsState>((set, get) => ({
  settings: null,
  isLoading: false,
  isSaving: false,
  error: null,

  storage: EMPTY_STORAGE,
  storageForbidden: false,
  isTestingStorage: false,
  storageTestResult: null,

  fetchSettings: async () => {
    set({ isLoading: true, error: null });
    try {
      const res = await apiClient.get('/assets/asset-settings');
      set({ settings: res.data?.data ?? res.data, isLoading: false });
    } catch (e) {
      set({ isLoading: false, error: errorOf(e, 'Could not load asset settings') });
    }
  },

  saveSettings: async (patch, successMessage = 'Settings saved') => {
    set({ isSaving: true });
    try {
      const res = await apiClient.put('/assets/asset-settings', patch);
      const saved: AssetSettings = res.data?.data ?? res.data;
      set({ settings: saved, isSaving: false });
      toast.success(successMessage);
      return saved;
    } catch (e) {
      set({ isSaving: false });
      const msg = errorOf(e, 'Could not save settings');
      toast.error(msg);
      throw new Error(msg);
    }
  },

  fetchBackendStorageConfig: async () => {
    try {
      const res = await apiClient.get('/assets/storage/config');
      const data = res.data?.data || res.data;
      if (data) {
        set({
          storage: {
            s3_bucket: data.s3Bucket || '',
            s3_region: data.s3Region || '',
            s3_prefix: data.s3Prefix || '',
            s3_endpoint: data.s3Endpoint || '',
            force_path_style: Boolean(data.forcePathStyle),
            direct_upload_enabled: data.directUploadEnabled ?? true,
            max_file_size_mb: data.maxFileSizeMb ?? 25,
            is_configured: Boolean(data.isConfigured),
            access_key_preview: data.accessKeyPreview || '',
          },
        });
      }
    } catch (e: any) {
      if (e?.response?.status === 403) set({ storageForbidden: true });
      else set({ error: errorOf(e, 'Could not load storage settings') });
    }
  },

  saveBackendStorageConfig: async (updates) => {
    const target = { ...get().storage, ...(updates || {}) };
    try {
      const res = await apiClient.put('/assets/storage/config', {
        s3Bucket: target.s3_bucket,
        s3Region: target.s3_region,
        s3Prefix: target.s3_prefix,
        accessKeyId: target.access_key_id || undefined,
        secretAccessKey: target.secret_access_key || undefined,
        s3Endpoint: target.s3_endpoint,
        forcePathStyle: target.force_path_style,
        directUploadEnabled: target.direct_upload_enabled,
        maxFileSizeMb: target.max_file_size_mb,
      });
      const data = res.data?.data || res.data || {};
      // Keep only non-secret fields in memory.
      const { access_key_id: _k, secret_access_key: _s, ...safe } = target;
      set({ storage: { ...safe, is_configured: data.isConfigured, access_key_preview: data.accessKeyPreview } });
      toast.success('Storage settings saved');
    } catch (e) {
      toast.error(`Could not save storage settings: ${errorOf(e, 'unknown error')}`);
    }
  },

  testStorageConnection: async () => {
    set({ isTestingStorage: true, storageTestResult: null });
    try {
      const res = await apiClient.post('/assets/storage/test-connection');
      const data = res.data?.data || res.data;
      set({ isTestingStorage: false, storageTestResult: data });
      if (data?.success) toast.success(data.message || 'Storage connection works');
      else toast.error(data?.message || 'Storage connection failed');
      return data;
    } catch (e) {
      const result = { success: false, message: errorOf(e, 'Could not reach the storage service') };
      set({ isTestingStorage: false, storageTestResult: result });
      toast.error(result.message);
      return result;
    }
  },
}));
